import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { get, isEmpty, isNil, isNull } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import type { POSTAddFileAdvancementCommentReq } from '@appTypes/esb';
import type { AddAdvancementComment } from '@appTypes/esb/advancements/v2/users/_userId/comments/add';
import {
  useAddAdvCommentsMutation,
  useAddFileAdvCommentsMutation,
  useDeleteAdvCommentMutation,
  useDeleteAdvCommentPhotoMutation,
  useEditAdvCommentMutation,
  useFetchAdvCommentsQuery,
} from '@modules/advancement/api';
import { advancementCommentSubject } from '@modules/advancement/constants';
import { advCommentsFound } from '@modules/youthProfile/duck/actions';
import { youthAdvRequirementsSel } from '@modules/youthProfile/duck/selectors';
import { transformComments } from '@modules/youthProfile/utils';
import { advancementStatuses, advancementTypes } from '@shared/constants';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';
import { userIdSel as loggedInUserIdSel } from '@user';

export interface TransformedCommentPhoto {
  id: number;
  smallImage: string;
  mediumImage: string;
  largeImage: string;
  dateCreated: string;
  title: string;
  description: string;
  pending: boolean;
  sortOrder: string | null;
}

export interface TransformedComments {
  author: string;
  authorImage: string;
  date: string;
  id: number;
  message: string;
  photos: TransformedCommentPhoto[];
  userId: number;
}

interface UploadedImage {
  description: string;
  requirementId?: number;
  url: string;
}

const maxPollCount = 10;

const toOptionalNumber = (value?: string | number | null) =>
  isNil(value) || value === '' ? undefined : Number(value);

const isSameRequirement = (
  commentRequirementId: number | null,
  requirementId?: number,
) =>
  requirementId
    ? Number(commentRequirementId) === requirementId
    : isNull(commentRequirementId);

/**
 * Args shared by every subscriber so they all hit the same cache entry.
 * versionId is optional: some advancements (e.g. unit/MBC rows) have none.
 */
const getFetchAdvCommentsArgs = (
  youthUserId: number,
  advancementId: number,
  advancementType: advancementTypes,
  versionId?: string | number | null,
) => {
  const parsedVersionId = toOptionalNumber(versionId);

  return {
    // normalized so route params (strings) and context values share the cache entry
    youthUserId: Number(youthUserId),
    payload: {
      advancementType,
      advancementId: Number(advancementId),
      ...(isNil(parsedVersionId) ? {} : { versionId: parsedVersionId }),
    },
  };
};

/**
 * Lightweight subscription returning only the number of comments for a requirement
 * (or for the advancement itself when no requirementId is given).
 */
export const useAdvancementCommentsCount = (
  youthUserId: number,
  advancementId: number,
  advancementType: advancementTypes,
  versionId?: string | number | null,
  requirementId?: number,
) =>
  useFetchAdvCommentsQuery(
    getFetchAdvCommentsArgs(
      youthUserId,
      advancementId,
      advancementType,
      versionId,
    ),
    {
      skip: !(advancementId && youthUserId),
      selectFromResult: ({ data, isLoading }) => ({
        count: (data ?? []).filter(comment =>
          isSameRequirement(comment.advancementRequirementId, requirementId),
        ).length,
        loading: isLoading,
      }),
    },
  );

export const useAdvancementComments = (
  youthUserId: number,
  advancementId: number,
  advancementType: advancementTypes,
  versionId?: string | number | null,
  requirementId?: number,
  {
    syncStartedStatus = false,
  }: {
    /**
     * Only for the youth requirements page: commenting a not started advancement
     * starts it on the backend, so mirror that in the youthProfile state.
     * Elsewhere that state belongs to another youth/advancement.
     */
    syncStartedStatus?: boolean;
  } = {},
) => {
  const dispatch = useDispatch();
  const youthAdvancement = useSelector(youthAdvRequirementsSel);
  const [pollingInterval, setPollingInterval] = useState(0); // miliseconds
  const [pollCount, setPollCount] = useState(0);
  const [uploadedImages, setUploadImage] = useState<UploadedImage[]>([]);
  const uploadedImagesRef = useRef(uploadedImages);
  const hadPendingRef = useRef(false);
  const loggedInUserId = useSelector(loggedInUserIdSel);

  const parsedVersionId = toOptionalNumber(versionId);
  const skip = !(advancementId && youthUserId);
  const req = getFetchAdvCommentsArgs(
    youthUserId,
    advancementId,
    advancementType,
    versionId,
  );

  const { data, isLoading, isFetching } = useFetchAdvCommentsQuery(req, {
    pollingInterval,
    skip,
  });

  const [addAdvComments, { isLoading: isAddComment }] =
    useAddAdvCommentsMutation();
  const [addFileAdvComments, { isLoading: isAddingFile }] =
    useAddFileAdvCommentsMutation();
  const [editAdvComment, { isLoading: isEditing }] =
    useEditAdvCommentMutation();
  const [deleteAdvComment, { isLoading: isDeleting }] =
    useDeleteAdvCommentMutation();
  const [deleteAdvCommentPhoto, { isLoading: isDeletingPhoto }] =
    useDeleteAdvCommentPhotoMutation();

  const loading =
    isLoading ||
    isAddComment ||
    isAddingFile ||
    isEditing ||
    isDeleting ||
    isDeletingPhoto;

  const comments: TransformedComments[] = useMemo(() => {
    const usedImages = new Set<string>();

    return transformComments(data)
      .filter(comment =>
        isSameRequirement(comment.requirementId, requirementId),
      )
      .map(comment => {
        const photos = comment.photos.map(photo => {
          if (!photo.pending) {
            return photo;
          }

          const uploadedImage = uploadedImages.find(
            image =>
              !usedImages.has(image.url) &&
              image.description === comment.message &&
              toOptionalNumber(image.requirementId) ===
                toOptionalNumber(comment.requirementId),
          )?.url;

          if (!uploadedImage) {
            return photo;
          }

          usedImages.add(uploadedImage);

          return {
            ...photo,
            smallImage: uploadedImage,
            mediumImage: uploadedImage,
            largeImage: uploadedImage,
          };
        });

        return {
          ...comment,
          photos,
        };
      });
  }, [data, requirementId, uploadedImages]);

  const hasPending = useMemo(
    () =>
      comments?.some((item: TransformedComments) =>
        item.photos?.some(photo => photo.pending === true),
      ),
    [comments],
  );

  const addComment = useCallback(
    async (body: string, photo?: File) => {
      try {
        if (photo) {
          const payload: POSTAddFileAdvancementCommentReq = {
            advancementType,
            advancementId: Number(advancementId),
            body,
            subject: advancementCommentSubject,
            scoutUserId: Number(youthUserId),
            fileContentType: photo.type,
            versionId: parsedVersionId,
          };

          if (requirementId) {
            payload.requirementId = Number(requirementId);
          }

          await addFileAdvComments({
            userId: loggedInUserId,
            photo,
            payload,
          }).unwrap();

          setUploadImage(state => [
            ...state,
            {
              description: body,
              requirementId,
              url: URL.createObjectURL(photo),
            },
          ]);

          setPollCount(0);
        } else {
          const payload: AddAdvancementComment = {
            advancementType,
            body,
            scoutUserId: Number(youthUserId),
            subject: advancementCommentSubject,
            versionId: parsedVersionId,
          };

          if (advancementType === advancementTypes.SSELECTIVES) {
            payload.ssElectiveId = Number(advancementId);
          } else {
            payload.advancementId = Number(advancementId);
          }

          if (requirementId) {
            payload.requirementId = Number(requirementId);
          }
          const requestBody = {
            userId: Number(youthUserId),
            payload,
          };

          await addAdvComments(requestBody).unwrap();
        }

        toastService.success(
          intl.formatMessage({
            id: 'AdvRequirementsComments.addCommentSuccess',
          }),
        );
      } catch (error) {
        toastService.error(
          get(error, 'errorDesc') ||
            intl.formatMessage({
              id: 'AdvRequirementsComments.addCommentError',
            }),
        );

        throw error;
      }
    },
    [
      addAdvComments,
      addFileAdvComments,
      advancementId,
      advancementType,
      loggedInUserId,
      parsedVersionId,
      requirementId,
      youthUserId,
    ],
  );

  const editComment = useCallback(
    async (userId: number, commentId: number, comments: string) => {
      try {
        const payload = {
          comments,
          subject: 'subject-edit',
          versionId: parsedVersionId,
        };

        await editAdvComment({
          advancementId,
          commentId,
          payload,
          userId,
          youthUserId,
        }).unwrap();

        toastService.success(
          intl.formatMessage({
            id: 'AdvRequirementsComments.updateCommentSuccess',
          }),
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        toastService.error(
          intl.formatMessage({
            id: 'AdvRequirementsComments.updateCommentError',
          }),
        );
      }
    },
    [advancementId, editAdvComment, parsedVersionId, youthUserId],
  );

  const deleteComment = useCallback(
    async (userId: number, commentId: number) => {
      try {
        await deleteAdvComment({
          advancementId,
          commentId,
          userId,
          youthUserId,
        }).unwrap();

        toastService.success(
          intl.formatMessage({
            id: 'AdvRequirementsComments.deleteCommentSuccess',
          }),
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        toastService.error(
          intl.formatMessage({
            id: 'AdvRequirementsComments.deleteCommentError',
          }),
        );
      }
    },
    [advancementId, deleteAdvComment, youthUserId],
  );

  const deleteCommentPhoto = useCallback(
    async (userId: number, commentId: number, photoId: number) => {
      try {
        await deleteAdvCommentPhoto({
          advancementId,
          commentId,
          photoId,
          userId,
          youthUserId,
        }).unwrap();

        toastService.success(
          intl.formatMessage({
            id: 'AdvRequirementsComments.deleteCommentPhotoSuccess',
          }),
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        toastService.error(
          intl.formatMessage({
            id: 'AdvRequirementsComments.deleteCommentPhotoError',
          }),
        );
      }
    },
    [advancementId, deleteAdvCommentPhoto, youthUserId],
  );

  useEffect(() => {
    if (isFetching && hasPending) {
      setPollCount(prev => prev + 1);
    }
  }, [hasPending, isFetching]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!hasPending) {
      setPollingInterval(0);
      setPollCount(0);
      return;
    }

    if (pollCount >= maxPollCount) {
      setPollingInterval(0);
      return;
    }

    setPollingInterval(6000);
  }, [loading, hasPending, pollCount]);

  useEffect(() => {
    if (
      syncStartedStatus &&
      !isEmpty(data) &&
      youthAdvancement?.status === advancementStatuses.NOT_STARTED
    ) {
      dispatch(advCommentsFound(youthAdvancement));
    }
  }, [data, dispatch, syncStartedStatus, youthAdvancement]);

  useEffect(() => {
    uploadedImagesRef.current = uploadedImages;
  }, [uploadedImages]);

  // Once every pending photo is processed the local previews are no longer shown
  useEffect(() => {
    if (hadPendingRef.current && !hasPending && uploadedImages.length > 0) {
      uploadedImages.forEach(({ url }) => URL.revokeObjectURL(url));
      setUploadImage([]);
    }

    hadPendingRef.current = hasPending;
  }, [hasPending, uploadedImages]);

  useEffect(
    () => () =>
      uploadedImagesRef.current.forEach(({ url }) => URL.revokeObjectURL(url)),
    [],
  );

  return {
    addComment,
    comments,
    deleteComment,
    deleteCommentPhoto,
    editComment,
    loading,
  };
};
